import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useConnectionsJson, useConnectionsDraftJson, useSaveConnectionsJson, useSaveDraftConnectionsJson } from '@/hooks/useConnections';
import { useConnections, useDeleteConnection, useSyncConnection } from '@/hooks/useConnectionMutations';
import { usePermission } from '@/hooks/useUserPermissions';
import { useConnectionStore } from '@/stores/connectionStore';
import { useNotificationStore } from '@/stores/notificationStore';
import type { AvailableConnection } from '@/types';
import type {
  AgentConnection,
  ConnectionsConfiguration,
  McpConnection,
} from '@/types/logicApps';
import type { ServiceProviderConnection } from '@/types/connections';
import { telemetryService } from '@/services/telemetryService';

export type ConnectionsTabValue =
  | 'managed'
  | 'serviceProvider'
  | 'agent'
  | 'function'
  | 'mcp'
  | 'raw';

/**
 * Discriminator for connection families that live inside connections.json
 * (or connections-draft.json in draft mode). Used to route the shared
 * delete/save handlers to the right map + telemetry event.
 */
export type LocalConnectionKind = 'serviceProvider' | 'agent' | 'mcp';

/** Value shape accepted by `handleSaveLocalConnection`, keyed by kind. */
export type LocalConnectionValue = {
  serviceProvider: ServiceProviderConnection;
  agent: AgentConnection;
  mcp: McpConnection;
};

export interface UseConnectionsEditorOptions {
  projectName: string;
  appName: string;
  appId: string;
  /**
   * When false, all data fetches are skipped and state is reset. Use to gate
   * fetches behind dialog-open / app-selected.
   */
  enabled: boolean;
}

export interface ConnectionsEditorState {
  // Identity
  projectName: string;
  appName: string;

  // Tabs
  selectedTab: ConnectionsTabValue;
  setSelectedTab: (value: ConnectionsTabValue) => void;

  // Permissions
  userCanEdit: boolean;
  permissionsLoading: boolean;

  // Managed connections data
  connections: AvailableConnection[] | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  getConnectorId: (connection: AvailableConnection) => string;
  handleDeleteConnection: (connection: AvailableConnection) => Promise<void>;
  handleSyncConnection: (connection: AvailableConnection) => Promise<void>;
  isSyncing: boolean;

  // JSON-backed connection mutations (service provider, agent, MCP). Both
  // honor `isDraftMode` — reads from + writes to connections-draft.json when
  // draft mode is on, connections.json otherwise. The `kind` argument picks
  // the right map inside the connections file.
  handleDeleteLocalConnection: (id: string, kind: LocalConnectionKind) => Promise<void>;
  handleSaveLocalConnection: <K extends LocalConnectionKind>(
    id: string,
    kind: K,
    value: LocalConnectionValue[K],
  ) => Promise<void>;

  // Agent connections data (from connections.json)
  agentConnections: Record<string, AgentConnection> | undefined;
  // Service provider connections data (from connections.json)
  serviceProviderConnections: Record<string, ServiceProviderConnection> | undefined;
  // MCP connections data (from connections.json)
  mcpConnections: Record<string, McpConnection> | undefined;
  /** True while connections.json is still being fetched (used to avoid a premature empty-state flash) */
  isJsonLoading: boolean;
  /** True if the connections.json fetch errored */
  isJsonError: boolean;
  /** The error from the connections.json fetch, if any */
  jsonFetchError: Error | null;
  /** Call to retry the connections.json fetch */
  refetchConnectionsJson: () => void;

  // JSON editor data
  rawJson: string;
  jsonError: string | null;
  hasChanges: boolean;
  isValidJson: boolean;
  isSaving: boolean;
  handleJsonChange: (value: string) => void;
  handleSave: () => void | Promise<void>;
  handleDiscard: () => void;

  // Draft mode for JSON tab
  isDraftMode: boolean;
  setIsDraftMode: (value: boolean) => void;
  isDraftLoading: boolean;
  isDraftError: boolean;
  refetchDraftJson: () => void;
}

/**
 * Shared state hook for the Connections editor. Backs both the dialog and the
 * full-page variants so the data flow, permission gating, and JSON edit state
 * are identical regardless of presentation chrome.
 */
export function useConnectionsEditor({
  projectName,
  appName,
  appId,
  enabled,
}: UseConnectionsEditorOptions): ConnectionsEditorState {
  const automationAppId = useMemo(
    () => (projectName && appName ? `/automationProjects/${projectName}/applications/${appName}` : undefined),
    [projectName, appName],
  );

  const { data: userCanEdit = false, isLoading: permissionsLoading } = usePermission(
    enabled ? projectName : '',
    enabled ? appName : undefined,
    ['Contributor'],
  );

  const [selectedTab, setSelectedTab] = useState<ConnectionsTabValue>('managed');
  const [rawJson, setRawJson] = useState('');
  const [originalJson, setOriginalJson] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [isDraftMode, setIsDraftMode] = useState(false);

  const {
    data: connections,
    isLoading,
    isError,
    refetch,
  } = useConnections(enabled ? automationAppId : undefined, undefined, enabled);

  const { data: connectionsJson, isLoading: isJsonLoading, isError: isJsonError, error: jsonFetchError, refetch: refetchConnectionsJson } = useConnectionsJson(
    enabled ? { projectName, appName, appId } : null,
  );

  const { data: draftConnectionsJson, isLoading: isDraftLoading, isError: isDraftError, refetch: refetchDraftJson } = useConnectionsDraftJson(
    enabled ? { projectName, appName, appId } : null,
  );

  const deleteMutation = useDeleteConnection(connectionsJson ?? {}, draftConnectionsJson ?? {});
  const syncMutation = useSyncConnection();
  const removeConnectionReference = useConnectionStore((state) => state.removeConnectionReference);
  const addNotification = useNotificationStore((s) => s.addNotification);
  const saveMutation = useSaveConnectionsJson(
    enabled && appId ? { projectName, appName, appId } : null,
  );
  const saveDraftMutation = useSaveDraftConnectionsJson(
    enabled && appId ? { projectName, appName, appId } : null,
  );

  // Reset editor + tab whenever the target app identity changes. Without
  // this reset, unsaved edits made against the previous app's
  // connections.json would be preserved by the seed effect (which only
  // re-seeds when there are no pending edits) and could end up being saved
  // against the new app — corrupting the wrong file. Skip the initial mount
  // so we don't clobber the first seed. This effect MUST be declared before
  // the seed effect: when both deps change in the same render (e.g., the
  // dialog is reopened against a different app and connections.json
  // arrives) effects fire in source order, and we need the reset to run
  // first so the seed can repopulate cleanly afterwards.
  const isFirstAppMount = useRef(true);
  useEffect(() => {
    if (isFirstAppMount.current) {
      isFirstAppMount.current = false;
      return;
    }
    setSelectedTab('managed');
    setRawJson('');
    setOriginalJson('');
    setJsonError(null);
  }, [projectName, appName, appId]);

  // Seed (or re-seed) the editor whenever the upstream connections.json changes,
  // as long as the user has no unsaved edits. The previous implementation gated
  // re-seeding behind a one-shot `isInitialized` flag, which meant a `refetch`
  // (or any later cache update) never refreshed the editor — leaving the JSON
  // tab stale or empty if the first fetch landed before the file was ready.
  //
  // When isDraftMode is true, seed from draftConnectionsJson; otherwise use
  // production connectionsJson.
  const activeConnectionsJson = useMemo(() => (isDraftMode ? draftConnectionsJson : connectionsJson), [isDraftMode, draftConnectionsJson, connectionsJson]);
  const activeIsLoading = useMemo(() => (isDraftMode ? isDraftLoading : isJsonLoading), [isDraftMode, isDraftLoading, isJsonLoading]);

  useEffect(() => {
    if (!enabled) {
      // Reset when the editor is closed / no app selected so we re-seed cleanly
      // next time it opens.
      setRawJson('');
      setOriginalJson('');
      setJsonError(null);
      return;
    }
    if (activeIsLoading || !activeConnectionsJson) return;

    const json = JSON.stringify(activeConnectionsJson, null, 2);
    setOriginalJson((prevOriginal) => {
      // Only re-seed the editor's working copy when the user hasn't started
      // editing — otherwise their unsaved changes would be wiped out by a
      // background refetch.
      setRawJson((prevRaw) => (prevRaw === prevOriginal ? json : prevRaw));
      return json;
    });
  }, [enabled, activeConnectionsJson, activeIsLoading]);

  // Re-seed immediately when draft mode is toggled (force reseed by resetting
  // original so the next effect run will see prevRaw === prevOriginal).
  const prevDraftMode = useRef(isDraftMode);
  useEffect(() => {
    if (prevDraftMode.current !== isDraftMode) {
      prevDraftMode.current = isDraftMode;
      const source = isDraftMode ? draftConnectionsJson : connectionsJson;
      if (source) {
        const json = JSON.stringify(source, null, 2);
        setRawJson(json);
        setOriginalJson(json);
        setJsonError(null);
      }
    }
  }, [isDraftMode, draftConnectionsJson, connectionsJson]);

  const hasChanges = rawJson !== originalJson;
  const isValidJson = jsonError === null;

  const handleJsonChange = useCallback((value: string) => {
    setRawJson(value);
    try {
      JSON.parse(value);
      setJsonError(null);
    } catch (e) {
      setJsonError((e as Error).message);
    }
  }, []);

  const handleSave = useCallback(async () => {
    let parsed: ConnectionsConfiguration;
    try {
      parsed = JSON.parse(rawJson) as ConnectionsConfiguration;
    } catch (e) {
      setJsonError((e as Error).message);
      return;
    }

    try {
      if (isDraftMode) {
        await saveDraftMutation.mutateAsync(parsed);
      } else {
        await saveMutation.mutateAsync(parsed);
      }
      setOriginalJson(rawJson);
      addNotification({
        type: 'success',
        title: 'Connections saved',
        message: 'connections.json has been updated.',
      });
      telemetryService.trackEvent('connections.saved');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save connections';
      addNotification({
        type: 'error',
        title: 'Save failed',
        message,
      });
      telemetryService.trackException(error as Error, { context: 'connections.save' });
    }
  }, [rawJson, isDraftMode, addNotification, saveDraftMutation, saveMutation]);

  const handleDiscard = useCallback(() => {
    setRawJson(originalJson);
    setJsonError(null);
  }, [originalJson]);

  const handleDeleteConnection = useCallback(
    async (connection: AvailableConnection) => {
      try {
        await deleteMutation.mutateAsync({ connectionName: connection.name, connectorId: connection.connectorId ?? '' });
        // Keep the node-editor connection store in sync so freshly opened
        // panels don't reference the now-deleted connection.
        removeConnectionReference(connection.name);
        void refetch();
        addNotification({
          type: 'success',
          title: 'Connection deleted',
          message: `${connection.displayName ?? connection.name} was removed.`,
        });
        telemetryService.trackEvent('connection.deleted');
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to delete connection';
        addNotification({
          type: 'error',
          title: 'Delete failed',
          message,
        });
        telemetryService.trackException(error as Error, { context: 'connection.delete' });
        // Re-throw so the caller (e.g. the confirmation dialog) can keep its
        // pending state and let the user retry without closing.
        throw error;
      }
    },
    [deleteMutation, removeConnectionReference, refetch, addNotification],
  );

  const handleSyncConnection = useCallback(
    async (connection: AvailableConnection) => {
      try {
        await syncMutation.mutateAsync(connection.name);
        void refetch();
        addNotification({
          type: 'success',
          title: 'Connection synced',
          message: `${connection.displayName ?? connection.name} was synchronized.`,
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to sync connection';
        addNotification({
          type: 'error',
          title: 'Sync failed',
          message,
        });
        telemetryService.trackException(error as Error, { context: 'connection.sync' });
        throw error;
      }
    },
    [syncMutation, refetch, addNotification],
  );

  const getConnectorId = useCallback(
    (connection: AvailableConnection) => {
      const connector = connection.connectorId ?? '';
      // Canonical /managedApis/{name} format — expand to the full data-plane
      // path so downstream connector-metadata lookups can resolve it.
      const match = connector.match(/^\/managedApis\/([^/]+)$/i);
      if (match?.[1]) {
        return `/automationProjects/${projectName}/managedApis/${match[1]}`;
      }
      return connector;
    },
    [projectName],
  );

  // ── JSON-backed connection mutations ────────────────────────────────────
  // Service-provider / agent / MCP connections live inside connections.json
  // (or connections-draft.json in draft mode) rather than as ARM resources,
  // so edit/delete is a JSON patch on the file followed by a save. The
  // active source + save mutation are picked off `isDraftMode` so the user's
  // current view (Draft vs Production) is the one mutated.
  const mutateActiveConnections = useCallback(
    async (
      mutator: (current: ConnectionsConfiguration) => ConnectionsConfiguration,
      successMessage: { title: string; message: string; event: string },
      failureContext: string,
    ): Promise<void> => {
      // Snapshot the current source so concurrent draft-mode toggles can't
      // swap the target file under our feet between read and write.
      const draftMode = isDraftMode;
      const source = draftMode ? draftConnectionsJson : connectionsJson;
      if (!source) {
        // Nothing loaded yet — refuse rather than seeding an empty file that
        // would clobber out-of-band edits when the real fetch lands.
        throw new Error('Connections file is not loaded yet.');
      }
      const next = mutator(source);

      // Skip the round-trip when the mutator produced a byte-identical
      // file. Callers still pass through a fresh object graph on every
      // edit (record spreads always allocate), so reference equality is
      // never enough — compare canonical JSON. Common triggers:
      //   - Save on an SP dialog where the display-name was edited then
      //     reverted to the original value.
      //   - Delete of an id that isn't present in the map (delete no-op).
      //   - Save with a value deep-equal to what's already stored.
      // The user still gets the success notification so the UX matches a
      // real save, but we avoid an unnecessary POST + refetch.
      if (JSON.stringify(next) === JSON.stringify(source)) {
        addNotification({
          type: 'success',
          title: successMessage.title,
          message: successMessage.message,
        });
        telemetryService.trackEvent(`${successMessage.event}.noop`);
        return;
      }

      try {
        if (draftMode) {
          await saveDraftMutation.mutateAsync(next);
        } else {
          await saveMutation.mutateAsync(next);
        }
        addNotification({
          type: 'success',
          title: successMessage.title,
          message: successMessage.message,
        });
        telemetryService.trackEvent(successMessage.event);
        if (draftMode) {
          await refetchDraftJson();
        } else {
          await refetchConnectionsJson();
        }
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Failed to update connection';
        addNotification({ type: 'error', title: 'Update failed', message });
        telemetryService.trackException(error as Error, { context: failureContext });
        throw error;
      }
    },
    [
      isDraftMode,
      draftConnectionsJson,
      connectionsJson,
      saveDraftMutation,
      saveMutation,
      addNotification,
      refetchDraftJson,
      refetchConnectionsJson,
    ],
  );

  // Per-kind config for the two shared handlers below. Keeps the family →
  // map-key / label / telemetry-slug mapping in one place so adding a new
  // JSON-backed connection family is a one-line edit here.
  const LOCAL_KIND_CONFIG: Record<
    LocalConnectionKind,
    {
      mapKey: 'serviceProviderConnections' | 'agentConnections' | 'agentMcpConnections';
      label: string;
      telemetrySlug: string;
    }
  > = useMemo(
    () => ({
      serviceProvider: {
        mapKey: 'serviceProviderConnections',
        label: 'Service provider connection',
        telemetrySlug: 'serviceProvider',
      },
      agent: {
        mapKey: 'agentConnections',
        label: 'Agent connection',
        telemetrySlug: 'agent',
      },
      mcp: {
        mapKey: 'agentMcpConnections',
        label: 'MCP connection',
        telemetrySlug: 'mcp',
      },
    }),
    [],
  );

  const handleDeleteLocalConnection = useCallback(
    async (id: string, kind: LocalConnectionKind): Promise<void> => {
      const { mapKey, label, telemetrySlug } = LOCAL_KIND_CONFIG[kind];
      await mutateActiveConnections(
        (current) => {
          const next = { ...(current[mapKey] ?? {}) };
          delete next[id];
          return { ...current, [mapKey]: next };
        },
        {
          title: 'Connection deleted',
          message: `${label} "${id}" was removed.`,
          event: `connection.${telemetrySlug}.deleted`,
        },
        `connection.${telemetrySlug}.delete`,
      );
    },
    [mutateActiveConnections, LOCAL_KIND_CONFIG],
  );

  const handleSaveLocalConnection = useCallback(
    async <K extends LocalConnectionKind>(
      id: string,
      kind: K,
      value: LocalConnectionValue[K],
    ): Promise<void> => {
      const { mapKey, label, telemetrySlug } = LOCAL_KIND_CONFIG[kind];
      await mutateActiveConnections(
        (current) => ({
          ...current,
          [mapKey]: {
            ...(current[mapKey] ?? {}),
            [id]: value,
          },
        }),
        {
          title: 'Connection saved',
          message: `${label} "${id}" was updated.`,
          event: `connection.${telemetrySlug}.saved`,
        },
        `connection.${telemetrySlug}.save`,
      );
    },
    [mutateActiveConnections, LOCAL_KIND_CONFIG],
  );

  return {
    projectName,
    appName,
    selectedTab,
    setSelectedTab,
    userCanEdit,
    permissionsLoading,
    connections,
    isLoading,
    isError,
    refetch,
    getConnectorId,
    handleDeleteConnection,
    handleSyncConnection,
    isSyncing: syncMutation.isPending,
    handleDeleteLocalConnection,
    handleSaveLocalConnection,
    // Tabbed lists follow draft mode just like the raw JSON editor does, so
    // template-applied placeholders (which only land in connections-draft.json)
    // appear in the same view the user is editing. The Connections-view filters
    // still hide unusable placeholder rows.
    agentConnections: activeConnectionsJson?.agentConnections,
    serviceProviderConnections: activeConnectionsJson?.serviceProviderConnections,
    mcpConnections: activeConnectionsJson?.agentMcpConnections,
    isJsonLoading,
    isJsonError,
    jsonFetchError: jsonFetchError ?? null,
    refetchConnectionsJson,
    rawJson,
    jsonError,
    hasChanges,
    isValidJson,
    isSaving: saveMutation.isPending,
    handleJsonChange,
    handleSave,
    handleDiscard,
    isDraftMode,
    setIsDraftMode,
    isDraftLoading,
    isDraftError,
    refetchDraftJson,
  };
}
